import { formatDateShort, formatMoney } from "../../utils/format"

// Wording for automatic payment approval. A company's bills are only paid from
// the customer's UT wallet by themselves when the customer allows it, so every
// message here says plainly who can take money and when.

const STATUS_TEXT = {
  pending: { label: "Waiting for your choice", tone: "amber" },
  off: { label: "Auto-pay off", tone: "slate" },
  on: { label: "Auto-pay on", tone: "emerald" },
  trusted: { label: "Auto-pay on · you paid them before", tone: "emerald" },
  not_needed: { label: "Your rented unit · always paid", tone: "slate" },
}

export const describeCompanyStatus = (company = {}) =>
  STATUS_TEXT[company.status] || STATUS_TEXT.pending

const billCountText = (count) => `${count} bill${count === 1 ? "" : "s"}`

export const describeWaitingBills = (company = {}) => {
  const count = Number(company.waitingCount || 0)
  if (!(count > 0)) return ""
  return `${billCountText(count)} waiting · ${formatMoney(company.waitingAmount)}`
}

export const describeRequest = (company = {}) =>
  company.requestedAt ? `Asked you on ${formatDateShort(company.requestedAt)}` : ""

export const countWaitingCompanies = (companies = []) =>
  (Array.isArray(companies) ? companies : []).filter((company) => Number(company.waitingCount || 0) > 0).length

const TRUST_NOTE = "Only enable Auto-Pay for payment requesters you trust."
const formatKes = (value) => `KES ${formatMoney(value)}`

// The warning before turning a company on. Turning it on pays the company's
// waiting bills straight away, so the person sees exactly what will happen.
// Here the company is called a payment requester and amounts show KES.
export const buildTurnOnConfirm = (company = {}, walletBalance = 0) => {
  const name = company.providerName || "This company"
  const waiting = Number(company.waitingAmount || 0)
  const wallet = Math.max(0, Number(walletBalance || 0))

  if (!(waiting > 0)) {
    return {
      title: "Turn on Auto-Pay?",
      message: `${name}'s future bills will be paid from your wallet automatically.`,
      note: TRUST_NOTE,
      details: [{ label: "Payment requester", value: name }],
      confirmLabel: "Enable",
    }
  }

  const payNow = Math.min(wallet, waiting)
  let message = `Your wallet will automatically pay ${name}'s outstanding bills, starting with the oldest, and all future bills.`
  if (payNow < waiting) {
    message += payNow > 0
      ? ` Your wallet covers ${formatKes(payNow)} now. The rest is paid after you top up.`
      : " Your wallet is empty, so these bills are paid after you top up."
  }

  return {
    title: "Turn on Auto-Pay and pay now?",
    message,
    note: TRUST_NOTE,
    details: [
      { label: "Payment requester", value: name },
      { label: "Unpaid bills", value: `${billCountText(Number(company.waitingCount || 0))} · ${formatKes(waiting)}` },
      { label: "Your wallet", value: formatKes(wallet) },
    ],
    confirmLabel: payNow > 0 ? "Enable & Pay" : "Enable",
  }
}

const roundMoney = (value) => Math.round(Number(value || 0) * 100) / 100
// Rounds down to whole cents, so a suggested amount never exceeds the wallet.
const floorMoney = (value) => Math.floor(Number(value || 0) * 100 + 1e-6) / 100

// The most one hand payment can be: what the bill still owes, as far as the
// wallet reaches. The amount box starts here.
export const getMaxBillPayment = (bill = {}, walletBalance = 0) => floorMoney(Math.min(
  Math.max(0, Number(walletBalance || 0)),
  Math.max(0, Number(bill.outstandingAmount || 0)),
))

// Why the typed amount cannot be paid, or "" when it can. This checks the shape
// of a number the person typed, not what they meant.
export const getPayAmountError = (value, maxAmount) => {
  const text = String(value ?? "").trim()
  if (!text) return "Enter the amount you want to pay."
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return "Enter numbers only, with at most 2 decimals."
  const amount = Number(text)
  if (!(amount > 0)) return "Enter an amount greater than zero."
  if (amount > Number(maxAmount || 0) + 0.005) return `You can pay at most ${formatMoney(maxAmount)}.`
  return ""
}

// The check before paying one bill by hand. `chosenAmount` is what the person
// typed; without it the payment is as much of the bill as the wallet covers.
// The bill itself never changes: a smaller payment leaves the rest owed.
export const buildPayBillConfirm = (bill = {}, company = {}, walletBalance = 0, chosenAmount = null) => {
  const name = company.providerName || "this company"
  const due = Number(bill.outstandingAmount || 0)
  const wallet = Math.max(0, Number(walletBalance || 0))
  const maxPay = getMaxBillPayment(bill, wallet)
  const payNow = chosenAmount === null || chosenAmount === undefined
    ? maxPay
    : roundMoney(Math.min(Math.max(0, Number(chosenAmount) || 0), maxPay))
  let message = `This pays this one bill from your wallet. Auto-pay for ${name} stays ${company.approved ? "on" : "off"}.`
  if (!(payNow > 0)) message = "Enter how much of this bill you want to pay."
  else if (payNow < due) {
    message = `This pays ${formatMoney(payNow)} of the bill from your wallet. ${formatMoney(roundMoney(due - payNow))} will still be owed.`
  }

  return {
    title: "Pay this bill?",
    message,
    details: [
      { label: "Company", value: company.providerName || "Billing company" },
      { label: "Bill", value: bill.description || "Bill" },
      { label: "Amount due", value: formatMoney(due) },
      { label: "Your wallet", value: formatMoney(wallet) },
    ],
    confirmLabel: payNow > 0 ? `Pay ${formatMoney(payNow)}` : "Pay",
    payNow,
    maxPay,
  }
}

export const describeApprovalResult = ({ company = {}, approved, payment = {} } = {}) => {
  const name = company.providerName || "this company"
  if (!approved) return `Auto-pay is off for ${name}. New bills from them will wait for you.`
  const paid = Number(payment.totalPaid || 0)
  const waiting = Number(company.waitingAmount || 0)
  if (paid > 0 && paid + 0.005 < waiting) {
    return `Auto-pay is on for ${name}. ${formatMoney(paid)} paid now. The rest is paid after you top up.`
  }
  if (paid > 0) return `Auto-pay is on for ${name}. ${formatMoney(paid)} paid from your wallet.`
  if (waiting > 0) return `Auto-pay is on for ${name}. Their bills are paid after you top up.`
  return `Auto-pay is on for ${name}.`
}

export const describePaymentResult = ({ company = {}, result = {} } = {}) => {
  if (result.alreadyPaid) {
    return result.paidByCredit
      ? "This bill was paid from your credit with this company."
      : "This bill is already paid."
  }
  const name = company.providerName || "the company"
  const left = Number(result.outstandingAfter || 0)
  return `Paid ${formatMoney(result.amountPaid)} to ${name}.${left > 0 ? ` ${formatMoney(left)} is still owed.` : ""}`
}

// --- What the billing company sees about one customer ---

const CUSTOMER_CHIPS = {
  pending: { label: "Auto-pay not approved", tone: "amber" },
  off: { label: "Auto-pay turned off", tone: "slate" },
}

export const describeCustomerApprovalChip = (approval) =>
  (approval && CUSTOMER_CHIPS[approval.status]) || null

export const countCustomersAwaitingApproval = (customers = []) =>
  (Array.isArray(customers) ? customers : []).filter((customer) => customer?.autopayApproval?.status === "pending").length

export const describeRetry = (retryAt) =>
  retryAt ? `You can ask again on ${formatDateShort(retryAt)}.` : ""
